import { Yubinuki, Procedure, SasiType, NeedleMode } from './Yubinuki';
import DrawDirection from './DrawDirection';
import SasiPositionCounter from './SasiPositionCounter';

export interface SasiMukiViewModel {
    orderNumber: number,
    color: string,
    boxIndex: number,
    direction: number,
    width: number,
    stack: number,
}

export interface ChartScaleViewModel {
    koma: number,
    tobi: number,
    sasiMukis: Array<SasiMukiViewModel>,
}

export interface ItoDrawingViewModel {
    color: string,
    offset: number,
    deltaIndex: number,
    direction: number,
}

export interface ConnectedItoDrawingsGroup {
    drawings: Array<ItoDrawingViewModel>
}

const KomaResolutionBase = 20;

export const MathExt = {
    gcd: function(a: number, b: number): number {
        return ((b === 0) ? a : MathExt.gcd(b, a % b));
    },
    lcd: function(a: number, b: number): number {
        return a / MathExt.gcd(a, b) * b
    }
}

function translateToChartScaleViewModel(yubinuki: Yubinuki): ChartScaleViewModel {
    let sasiMukiViewModels = translateToSasiMukiViewModel(yubinuki);
    return {
        koma: yubinuki.koma,
        tobi: yubinuki.tobi,
        sasiMukis: sasiMukiViewModels,
    };
}

function translateToSasiMukiViewModel(yubinuki: Yubinuki): Array<SasiMukiViewModel> {
    let resolution = yubinuki.resolution;
    var stack = 0;
    var result: Array<SasiMukiViewModel> = [];

    yubinuki.procedures.forEach((proc, procedureIndex) => {
        let width = getStepsNeedToFill(yubinuki, procedureIndex) / resolution;
        result.push({
            orderNumber: procedureIndex + 1,
            color: getFirstItoColor(proc),
            boxIndex: getBoxIndex(proc),
            direction: getDirection(proc),
            width: Math.min(width, 1.0),
            stack: stack,
        });
        let [begin, tail] = getRangeOfRepeating(yubinuki, procedureIndex);
        if (begin === tail || tail === procedureIndex) {
            stack += 1
        }
    });
    return result;
}

function getFirstItoColor(procedure: Procedure): string {
    let ito = procedure.itos[0];
    if (ito.color.toUpperCase() === "FFFFFF") {
        return "gray";
    }
    return ito.color;
}

function getBoxIndex(procedure: Procedure): number {
    if (procedure.sasiType === SasiType.NAMI) {
        return procedure.offset
    } else { // HIRAKI
        return procedure.offset - 1;
    }
}

function getDirection(procedure: Procedure): number {
    if (procedure.sasiType === SasiType.NAMI) {
        return DrawDirection.RIGHT;
    } else { // HIRAKI
        return DrawDirection.LEFT;
    }
}

function getRangeOfRepeating(yubinuki: Yubinuki, procedureIndex: number): Array<number> {
    let targetProcedure = yubinuki.procedures[procedureIndex];

    if (targetProcedure.matomeSasi) {
        return [procedureIndex, procedureIndex];
    }

    var beginIndex = procedureIndex
    var tailIndex = procedureIndex

    while (0 < beginIndex) {
        let procedure = yubinuki.procedures[beginIndex - 1];
        if (procedure.matomeSasi || procedure.groupNo !== targetProcedure.groupNo) {
            break;
        }
        beginIndex -= 1;
    }

    while (tailIndex < yubinuki.procedures.length - 1) {
        let procedure = yubinuki.procedures[tailIndex + 1];
        if (procedure.matomeSasi || procedure.groupNo !== targetProcedure.groupNo) {
            break;
        }
        tailIndex += 1
    }

    return [beginIndex, tailIndex];
}

function translateToItoDrawingViewModel(yubinuki: Yubinuki): Array<ItoDrawingViewModel> {
    var result: Array<ItoDrawingViewModel> = [];
    for (var index = 0; index < yubinuki.procedures.length; index++) {
        var tmpModels: Array<Array<ConnectedItoDrawingsGroup>> = [];
        let repeatingRange = getRangeOfRepeating(yubinuki, index);

        if (repeatingRange[0] !== index) {
            continue;
        }

        for (var procedureIndex = repeatingRange[0]; procedureIndex <= repeatingRange[1]; procedureIndex++) {
            let stepsToFill = getStepsNeedToFill(yubinuki, procedureIndex);
            tmpModels.push(createConnectedItoDrawingGroups(yubinuki, procedureIndex, stepsToFill));
        }

        result.push(...verticalFlatMap(tmpModels));

        if ((yubinuki.needleMode === NeedleMode.SINGLE || yubinuki.procedures.length === 1) && index === 0) {
            break;
        }
    }

    return result;
}

function adjustResolution(yubinuki: Yubinuki): Yubinuki {
    let resolution = getIdealResolution(yubinuki);
    let clonedYubinuki: Yubinuki = JSON.parse(JSON.stringify(yubinuki));
    return Object.assign(clonedYubinuki, { resolution: resolution });
}

function getIdealResolution(yubinuki: Yubinuki): number {
    var resolution = yubinuki.resolution
    if (yubinuki.resolutionAuto) {
        let rounds = yubinuki.procedures.map(procedure => {
            let total = definedTotalRounds(procedure);
            let indefinite = indefiniteItoCount(procedure);
            return total + indefinite;
        });
        resolution = Math.max(rounds.reduce((a, b) => Math.max(a, b)), KomaResolutionBase);
    }
    return resolution;
}

function createConnectedItoDrawingGroups(yubinuki: Yubinuki, procedureIndex: number, stepsToFill: number): Array<ConnectedItoDrawingsGroup> {
    if (stepsToFill <= 0) {
        return [];
    }

    var result: Array<ConnectedItoDrawingsGroup> = []

    let procedure = yubinuki.procedures[procedureIndex];
    let indefiniteItos = indefiniteItoCount(procedure);
    let restForIndefinite = stepsToFill - definedTotalRounds(procedure);
    var remainder = (indefiniteItos === 0) ? 0 : restForIndefinite % indefiniteItos;
    let quotient = (indefiniteItos === 0) ? 0 : (restForIndefinite - remainder) / indefiniteItos;
    let resolution = yubinuki.resolution;

    let direction = getDirection(procedure);
    let connectedRounds = procedure.connectedRounds;

    let positionCounter = new SasiPositionCounter(yubinuki.tobi, resolution, procedure.offset, procedure.sasiType);

    var itoDrawings: Array<ItoDrawingViewModel> = []
    var drawingsCount = 0;

    for (var iIto = 0; iIto < procedure.itos.length; iIto++) {
        let ito = procedure.itos[iIto];
        let stepsUpTo: number;
        var goOutItoLoop = false
        if (ito.rounds === 0) {
            if (0 < remainder) {
                stepsUpTo = quotient + 1;
                remainder -= 1;
            } else {
                stepsUpTo = quotient;
            }
        } else {
            stepsUpTo = ito.rounds;
        }

        if (stepsUpTo <= 0) {
            continue;
        }

        for (var j = 1; j <= stepsUpTo; j++) {
            let itoDrawing = createItoDrawingViewModel(ito.color, positionCounter.offset, positionCounter.deltaIndex, direction);
            itoDrawings.push(itoDrawing);
            drawingsCount += 1

            positionCounter.tick()

            if (connectedRounds <= itoDrawings.length) {
                result.push(createConnectedItoDrawingGroup(itoDrawings));
                itoDrawings = [];
            }
            if (stepsToFill <= drawingsCount) {
                goOutItoLoop = true;
                break;
            }
        }
        if (goOutItoLoop) {
            break;
        }
    }
    if (0 < itoDrawings.length) {
        result.push(createConnectedItoDrawingGroup(itoDrawings));
    }

    return result;
}

function createConnectedItoDrawingGroup(itoDrawings: Array<ItoDrawingViewModel>): ConnectedItoDrawingsGroup {
    return { drawings: itoDrawings }
}

function createItoDrawingViewModel(color: string, offset: number, deltaIndex: number, direction: number): ItoDrawingViewModel {
    return { color: color, offset: offset, deltaIndex: deltaIndex, direction: direction }
}

function verticalFlatMap(array: Array<Array<ConnectedItoDrawingsGroup>>): Array<ItoDrawingViewModel> {
    var result: Array<ItoDrawingViewModel> = [];

    let arrayLength = array.length;
    var lengthCheck = arrayLength;
    var indexInArray = 0;

    while(0 < lengthCheck) {
        lengthCheck = 0;
        for (var i = 0; i < arrayLength; i++) {
            if (indexInArray < array[i].length) {
                result.push(...array[i][indexInArray].drawings);
                lengthCheck++;
            }
        }
        indexInArray++;
    }

    return result;
}

function getStepsNeedToFill(yubinuki: Yubinuki, procedureIndex: number): number {
    let targetProcedure = yubinuki.procedures[procedureIndex];

    if (yubinuki.needleMode === NeedleMode.SINGLE || yubinuki.procedures.length === 1) {
        if (procedureIndex === 0) {
            return maxProcedureCount(yubinuki.koma, yubinuki.tobi) * yubinuki.resolution;
        } else {
            return 0;
        }
    } else {
        let sameStateProcedures = getSameOffsetAndSasiTypeProcedures(yubinuki, targetProcedure);
        if (sameStateProcedures.length > 1) {
            let firstProcedure = sameStateProcedures[0];
            if (firstProcedure === targetProcedure) {
                if (sameStateProcedures.length === yubinuki.procedures.length) {
                    // すべてが同じ(offset, sasiType)のとき
                    return maxProcedureCount(yubinuki.koma, yubinuki.tobi) * yubinuki.resolution;
                }
            } else {
                return 0;
            }
        }

        let neighborProcedure = getForwardNeighborProcedure(yubinuki, targetProcedure);
        if (neighborProcedure === null) {
            console.warn("Something wrong, all procedures have same offset and sasiType. This state should be cathced before.");
            return 0;
        }

        if (targetProcedure.sasiType === neighborProcedure.sasiType) {
            return getStepsNeedtoFillBetweenSameDirection(yubinuki, targetProcedure, neighborProcedure);
        } else {
            return getStepsNeedToFillBetweenDifferenceDirection(yubinuki, targetProcedure, neighborProcedure);
        }
    }
}

/// [should] targetProcedure.sasiType === neightborProcedure.sasiType
function getStepsNeedtoFillBetweenSameDirection(yubinuki: Yubinuki, targetProcedure: Procedure, neighborProcedure: Procedure): number {
    let effectiveOffsetMax = maxProcedureCount(yubinuki.koma, yubinuki.tobi);
    let targetOffset = targetProcedure.offset % effectiveOffsetMax;
    let neighborOffset = neighborProcedure.offset % effectiveOffsetMax;
    var shouldFillWidth = 0
    switch (targetProcedure.sasiType) {
        case SasiType.NAMI:
            if (neighborOffset < targetOffset) {
                shouldFillWidth = neighborOffset + effectiveOffsetMax - targetOffset;
            } else {
                shouldFillWidth = neighborOffset - targetOffset;
            }
            break;
        case SasiType.HIRAKI:
            if (targetOffset < neighborOffset) {
                shouldFillWidth = targetOffset + effectiveOffsetMax - neighborOffset
            } else {
                shouldFillWidth = targetOffset - neighborOffset
            }
            break;
    }
    return shouldFillWidth * yubinuki.resolution;
}

/// [should] targetProcedure と neighborProcedure は、offsetが隣りあっている。
/// [should] targetProcedure と neighborProcedure は、刺す向きが向いあっている。
function getStepsNeedToFillBetweenDifferenceDirection(yubinuki: Yubinuki, targetProcedure: Procedure, neighborProcedure: Procedure): number {
    let targetAllDefined = (indefiniteItoCount(targetProcedure) === 0);
    let neighborAllDefined = (indefiniteItoCount(neighborProcedure) === 0);
    let betweenSteps = getStepsBetween(yubinuki, targetProcedure, neighborProcedure);
    let targetIndex = yubinuki.procedures.findIndex(proc => proc === targetProcedure);
    let neighborIndex = yubinuki.procedures.findIndex(proc => proc === neighborProcedure);

    if (targetAllDefined && neighborAllDefined) {
        let targetDefinedRounds = definedTotalRounds(targetProcedure);
        let neighborDefinedRounds = definedTotalRounds(neighborProcedure);
        let sumOfDefinedSteps = targetDefinedRounds + neighborDefinedRounds;
        if (sumOfDefinedSteps <= betweenSteps) {
            return targetDefinedRounds;
        } else if (onSameRepeatingGroup(yubinuki, targetProcedure, neighborProcedure)) {
            let halfOfSteps = betweenSteps / 2;
            if (halfOfSteps <= targetDefinedRounds && halfOfSteps <= neighborDefinedRounds) {
                return halfOfSteps;
            } else if (targetDefinedRounds < halfOfSteps) {
                return targetDefinedRounds;
            } else {
                return betweenSteps - neighborDefinedRounds;
            }
        } else {
            if (targetIndex < neighborIndex) {
                return targetDefinedRounds;
            } else {
                return betweenSteps - neighborDefinedRounds;
            }
        }
    } else if (targetAllDefined) {
        return definedTotalRounds(targetProcedure);
    } else if (neighborAllDefined) {
        let remain = betweenSteps - definedTotalRounds(neighborProcedure);
        return Math.max(remain, 0);
    } else {
        // どちらも未定義を含むとき、connectedRoundsの割合を考慮する。割り切れないときは、先の手順を優先する
        if (targetIndex < neighborIndex) {
            return firstDivisionSteps(betweenSteps, targetProcedure, neighborProcedure);
        } else {
            let firstDivision = firstDivisionSteps(betweenSteps, neighborProcedure, targetProcedure);
            return betweenSteps - firstDivision;
        }
    }
}

let maxProcedureCountCache: { [key: string]: number } = {}
function maxProcedureCount(koma: number, tobi: number): number {
    const cache = getMaxProcedureCountFromCache(koma, tobi);
    if (cache !== null) { return cache; }

    // 一段を埋めた時に使用するコマの数 = 最小公倍数(コマ, トビ) / トビ * 2;
    let usedProc = MathExt.lcd(koma, tobi) / tobi * 2
    // 全面を埋めるコマの数 = 地割りコマ * 2
    let fillingProc = koma * 2
    // 重複しない刺し始め(コマ位置)の数 =　全面を埋めるコマの数 /
    // 一段を埋めた時に使用するコマの数
    let result =  fillingProc / usedProc

    setMaxProcedureCountCache(koma, tobi, result);
    return result;
}

function getMaxProcedureCountFromCache(koma: number, tobi: number): number | null {
    const key = `${koma}_${tobi}`;
    if (Object.keys(maxProcedureCountCache).includes(key)) {
        return maxProcedureCountCache[key];
    } else {
        return null;
    }
}

function setMaxProcedureCountCache(koma: number, tobi: number, value: number) {
    const key = `${koma}_${tobi}`;
    maxProcedureCountCache[key] = value;
}

function indefiniteItoCount(procedure: Procedure): number {
    return procedure.itos.filter(ito => ito.rounds === 0).length
}

function definedTotalRounds(procedure: Procedure): number {
    return procedure.itos.map(ito => ito.rounds).reduce((p, v) => p + v, 0);
}

function firstDivisionSteps(betweenSteps: number, firstProcedure: Procedure, secondProcedure: Procedure) {
    let totalConnectedRounds = firstProcedure.connectedRounds + secondProcedure.connectedRounds;
    let rest = betweenSteps % totalConnectedRounds;
    let quotient = (betweenSteps - rest) / totalConnectedRounds;
    return quotient * firstProcedure.connectedRounds + Math.min(rest, firstProcedure.connectedRounds);
}

function onSameRepeatingGroup(yubinuki: Yubinuki, targetProcedure: Procedure, neighborProcedure: Procedure): boolean {
    if (targetProcedure.groupNo !== neighborProcedure.groupNo) {
        return false;
    }
    if (targetProcedure.matomeSasi || neighborProcedure.matomeSasi) {
        return false;
    }
    return true;
}

function getSameOffsetAndSasiTypeProcedures(yubinuki: Yubinuki, targetProcedure: Procedure): Array<Procedure> {
    let effectiveOffsetMax = maxProcedureCount(yubinuki.koma, yubinuki.tobi);
    let targetOffset = targetProcedure.offset % effectiveOffsetMax;
    return yubinuki.procedures.filter((proc) => {
        return proc.offset % effectiveOffsetMax === targetOffset && proc.sasiType === targetProcedure.sasiType;
    });
}

function getForwardNeighborProcedure(yubinuki: Yubinuki, targetProcedure: Procedure): Procedure | null {
    let effectiveOffsetMax = maxProcedureCount(yubinuki.koma, yubinuki.tobi);
    let clonedProcedures: Array<Procedure> = yubinuki.procedures.concat();
    let sortedProcedures = clonedProcedures.sort((a, b) => {
        let aOffset = a.offset % effectiveOffsetMax
        let bOffset = b.offset % effectiveOffsetMax
        if (aOffset === bOffset && a.sasiType !== b.sasiType) {
            if (a.sasiType === SasiType.HIRAKI) {
                return -1;
            }
        }
        if (aOffset < bOffset) { return -1; }
        if (aOffset > bOffset) { return 1; }
        return 0;
    });
    let targetIndex = sortedProcedures.findIndex((proc) => { return targetProcedure === proc; });

    let procedureLength = sortedProcedures.length;
    var index: number = targetIndex
    do {
        index = (targetProcedure.sasiType === SasiType.NAMI) ? index + 1 : index - 1;
        if (index < 0) {
            index += procedureLength;
        } else if (procedureLength <= index) {
            index -= procedureLength;
        }
        let neighborProcedure = sortedProcedures[index];
        if (targetProcedure.offset % effectiveOffsetMax !== neighborProcedure.offset % effectiveOffsetMax
            || targetProcedure.sasiType !== neighborProcedure.sasiType) {
                return neighborProcedure
            }
    } while (index !== targetIndex)

    return null
}

/// [should] a, b は sasiTypeが異なり、進行方向が向いあっている
function getStepsBetween(yubinuki: Yubinuki, a: Procedure, b: Procedure): number {
    let effectiveOffsetMax = maxProcedureCount(yubinuki.koma, yubinuki.tobi);
    let aOffset = a.offset % effectiveOffsetMax;
    let bOffset = b.offset % effectiveOffsetMax;
    var count = 0;
    var pointer = aOffset;

    if (a.sasiType === SasiType.NAMI) {
        do {
            pointer += 1;
            count += 1;
            if (pointer !== bOffset && effectiveOffsetMax <= pointer) {
                pointer -= effectiveOffsetMax;
            }
        } while (pointer !== bOffset);
    } else {
        do {
            pointer -= 1;
            count += 1;
            if (pointer !== bOffset && pointer <= 0) {
                pointer += effectiveOffsetMax;
            }
        } while (pointer !== bOffset);
    }
    return count * yubinuki.resolution;
}

export default {
    adjustResolution: adjustResolution,
    translateToChartScaleViewModel: translateToChartScaleViewModel,
    translateToSasiMukiViewModel: translateToSasiMukiViewModel,
    translateToItoDrawingViewModel: translateToItoDrawingViewModel,
    createConnectedItoDrawingGroups: createConnectedItoDrawingGroups,
    getRangeOfRepeating: getRangeOfRepeating,
    getStepsNeedToFill: getStepsNeedToFill,
    maxProcedureCount: maxProcedureCount,
    indefiniteItoCount: indefiniteItoCount,
    definedTotalRounds: definedTotalRounds,
    verticalFlatMap: verticalFlatMap,
}
